// Kit de Cambio de Vida.
//
// Cuando a un cliente le cambia la vida —se casa, tiene un hijo, se muda,
// pierde el trabajo o el seguro del trabajo— se le abre un período especial
// para inscribirse o cambiar de plan, y hay que pedirle papeles que lo
// prueben. Antes eso se escribía a mano cada vez, cliente por cliente
// (Lissette 2026-08-19, punto 8 de su lista).
//
// Es un kit APARTE del de bienvenida, con sus propios documentos y su propio
// mensaje:
//   • Los documentos se marcan en la pestaña Documentos del cliente, con el
//     botón "Kit ▾" → "🔄 Kit de Cambio de Vida" (justo debajo de Kit Portal).
//   • El mensaje se escribe una vez para toda la agencia AQUÍ MISMO, con el
//     botón "✏️ Editar el mensaje de la agencia" (Lissette 2026-10-26: "en
//     renovación no debería ir el kit life change, solo déjalo en la pestaña de
//     cada cliente"). Antes se editaba desde Renovaciones → Kit de bienvenida,
//     mezclado con los mensajes de renovación.
//
// El envío NO reinventa nada: va por la misma ruta que el correo normal de la
// ficha (`sendClientEmail`), así que hereda el encabezado de la agencia, la
// firma del agente, el pie de ley, los enlaces seguros de 7 días y el registro
// en "Enviados". Se manda desde el buzón del agente, como todo lo demás.
import { useEffect, useMemo, useState } from 'react';
import { Send, Paperclip } from 'lucide-react';
import { Modal, confirmDialog } from './ui.jsx';
import { toast } from './Toast.jsx';
import { getAgencySettings, updateAgencySettings, sendClientEmail } from '../api.js';
import { KIT_LIFE_CHANGE, getPolicyType, tiposDePolizaConKit } from '../policyTypes.js';
import { mensajeDeCambioDeVida, versionParaEditar, mapaConVersion, hayMensajePropio } from '../kitDeCambioDeVida.js';
import { chipsDelKit, rellenarCamposDelKit, valoresDePolizaParaElKit } from '../camposDelKit.js';
import { useVarInserter, VarChips } from './VarChips.jsx';
import EmailRichEditor from './EmailRichEditor.jsx';
import { aHtmlDeDocumento, esDocumentoConFormato } from '../documentoConFormato.js';

// Tope del backend (`docs` en POST /clients/:id/emails). Se respeta aquí para
// avisar antes de enviar en vez de comerse un 400.
const MAX_DOCS = 12;

const DEFAULT_ES = {
  subject: 'Cambio de vida — documentos que necesitamos, {cliente}',
  body: `Hola {cliente},

Nos comentaste que tuviste un cambio de vida (matrimonio, nacimiento, mudanza, pérdida de cobertura u otro). Eso te abre un período especial para inscribirte o cambiar tu plan, pero hay que enviarlo con comprobantes.

Abajo te dejo los documentos que necesitamos. Respóndeme a este correo con las fotos o los archivos y yo me encargo del resto.

Si tienes cualquier duda, escríbeme.`,
};

const DEFAULT_EN = {
  subject: 'Life change — documents we need, {cliente}',
  body: `Hi {cliente},

You told us you had a qualifying life event (marriage, birth, a move, loss of coverage, or another). That opens a special window to enroll or change your plan, but it has to be filed with proof.

Below are the documents we need. Reply to this email with photos or files and I will take care of the rest.

If anything is unclear, just write back.`,
};

// Mismos marcadores que el kit de bienvenida, y de la MISMA lista
// (`camposDelKit.js`) — no dos idiomas parecidos que se separan con el tiempo.
const fill = rellenarCamposDelKit;

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export default function LifeChangeKitModal({ client, docs = [], policies: polizasRecibidas = [], lang = 'es', onClose }) {
  const es = lang === 'es';

  // SOLO LAS PÓLIZAS DE ESTE CLIENTE. Lissette, 2026-10-27, con captura: el
  // kit de Roxandra decía "Su póliza de Salud con Kaiser Permanente" y ella no
  // tiene ninguna póliza con Kaiser. `ClientDetail` le pasaba `policies`, que
  // son las de TODA LA AGENCIA; cuando el cliente no tenía una del tipo
  // elegido, `vivas[0]` era la primera póliza de la agencia: la de otra
  // persona, con su compañía, su número y su prima. El mismo fallo que ya
  // tuvieron los documentos de este kit (Marilyn Sequera, 2026-09-01, más
  // abajo). Ahora ClientDetail pasa `clientPolicies`, y aquí se filtra igual
  // por si otra pantalla vuelve a pasar la lista entera.
  const policies = useMemo(
    () => (polizasRecibidas || []).filter((p) => p && p.clientId === client?.id),
    [polizasRecibidas, client?.id],
  );

  // Idioma del MENSAJE — distinto del idioma de esta ventana, que es el del
  // agente. Arranca en el que el cliente prefiere: mandarle en inglés a quien
  // pidió español es el fallo que se está evitando, y antes ni se veía cuál
  // se estaba usando (Lissette 2026-08-20).
  const [idioma, setIdioma] = useState(
    () => (String(client?.preferredLanguage || '').toLowerCase().startsWith('es') ? 'es' : 'en'),
  );

  // Tipo de seguro del mensaje. Un cambio de vida no se explica igual en Salud
  // que en Vida, así que cada tipo puede tener el suyo; si no lo tiene escrito,
  // se usa el General. Arranca en el tipo de la póliza que ya tiene el cliente.
  const tipoInicial = () => {
    const suyos = (policies || [])
      .filter((p) => String(p?.status || '').toLowerCase() !== 'cancelled')
      .map((p) => p?.type)
      .filter((t) => tiposDePolizaConKit().includes(t));
    return suyos[0] || 'general';
  };
  const [tipo, setTipo] = useState(tipoInicial);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [ajustes, setAjustes] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const vi = useVarInserter();
  // EDITAR EL MENSAJE DE LA AGENCIA, aquí y no en Renovaciones (2026-10-26).
  // Se edita la PLANTILLA (con sus {campos}), no el texto ya rellenado de este
  // cliente: lo que se guarda vale para todos los clientes de la agencia.
  const [editando, setEditando] = useState(false);
  const [plAsunto, setPlAsunto] = useState('');
  const [plCuerpo, setPlCuerpo] = useState('');
  const [guardandoPl, setGuardandoPl] = useState(false);
  const viPl = useVarInserter();

  // Los documentos de ESTE kit Y DE ESTE CLIENTE.
  //
  // ── EL FALLO QUE ARREGLA ────────────────────────────────────────────────
  //
  // Ticket de Marilyn Sequera, 2026-09-01: *"se han enviado dos correos de
  // cambio de vida y en la parte de documentos adjuntos aparecen los de otros
  // clientes"*.
  //
  // Y era cierto. `ClientDetail` recibe `docs` con los documentos de TODA LA
  // AGENCIA —no los de este cliente— y los filtra por cliente en cada sitio que
  // los usa (`clientDocs`, `_clientDocs`…). Este filtro se saltaba ese paso:
  // miraba solo el kit. Resultado: la lista mostraba los documentos de kit de
  // cambio de vida de CUALQUIER cliente de la agencia, con sus nombres —que
  // suelen llevar el nombre de la persona y la fecha— delante de quien
  // estuviera mandando el correo.
  //
  // Peor todavía: el efecto de abajo los MARCA TODOS de entrada, así que la
  // agente enviaba creyendo que iban quince documentos.
  //
  // Lo que NO llegó a pasar, y conviene dejarlo escrito para no asustar de más:
  // el servidor sí acota (`resolveEmailDocs` busca con `where: { id: {in},
  // agencyId, clientId }`), así que los archivos de otro cliente NUNCA se
  // adjuntaron ni se enviaron — se caían en silencio al mandar. La fuga fue de
  // NOMBRES en pantalla, dentro de la misma agencia, y el envío salía con menos
  // documentos de los que ella veía marcados. Dos fallos de un solo filtro.
  const kitDocs = useMemo(
    () => (docs || []).filter((d) => (
      d.clientId === client?.id
      && Array.isArray(d.kits) && d.kits.includes(KIT_LIFE_CHANGE)
    )),
    [docs, client?.id],
  );
  const [sel, setSel] = useState({});
  useEffect(() => {
    // Todos marcados de entrada: si el agente los puso en el kit es porque los
    // quiere mandar. Desmarcar uno suelto es más fácil que marcar quince.
    const inicial = {};
    kitDocs.slice(0, MAX_DOCS).forEach((d) => { inicial[d.id] = true; });
    setSel(inicial);
  }, [kitDocs]);

  // La poliza de la que salen {compania}, {tipo} y {poliza}: la del tipo
  // elegido arriba, y si el kit va "General" la primera que siga viva.
  //
  // Esto NO es un adorno. Este kit se manda por `sendClientEmail`, la ruta del
  // correo normal de la ficha, que en el servidor solo resuelve {portal} y la
  // firma del agente — NO pasa por `fillKitPlaceholders` (esa es del kit de
  // bienvenida, en policies.routes.ts). Si una ficha metiera {compania} sin
  // que se rellene aqui, al cliente le llegaria el correo con las llaves
  // puestas. Por eso las fichas y este objeto se agregaron a la vez.
  const poliza = useMemo(() => {
    const vivas = (policies || []).filter((p) => String(p?.status || '').toLowerCase() !== 'cancelled');
    return vivas.find((p) => p?.type === tipo) || vivas[0] || null;
  }, [policies, tipo]);

  const vars = useMemo(() => {
    const pt = poliza?.type ? getPolicyType(poliza.type) : null;
    return {
      cliente: `${client?.firstName || ''} ${client?.lastName || ''}`.trim() || (es ? 'cliente' : 'client'),
      compania: poliza?.carrier || '',
      tipo: pt ? (es ? (pt.labelEs || pt.label) : (pt.label || pt.labelEs)) : '',
      poliza: poliza?.policyNumber || '',
      // {plan} {ingreso} {prima} {efectividad} (Lissette 2026-10-16).
      ...valoresDePolizaParaElKit(poliza, client),
    };
  }, [client?.firstName, client?.lastName, client?.annualIncome, poliza, es]);

  useEffect(() => {
    let vivo = true;
    getAgencySettings()
      // Sin ajustes (o sin servidor) se sigue pudiendo mandar el kit con el
      // texto por defecto: quedarse en blanco sería peor.
      .then((s) => { if (vivo) setAjustes(s || {}); })
      .catch(() => { if (vivo) setAjustes({}); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, []);

  // El texto se rehace al cambiar de tipo o de idioma. Se pierde lo que se
  // hubiera retocado a mano, y así tiene que ser: cambiar de plantilla es
  // pedir OTRO texto, no editar el que había.
  useEffect(() => {
    if (ajustes === null) return;
    const W = ajustes.welcomeKitTemplates || {};
    // Su tipo primero; si ese no está escrito, el General; si tampoco, el de
    // fábrica. Todo EN EL IDIOMA ELEGIDO (E-420): antes el mensaje propio era
    // uno solo y salía igual en los dos idiomas.
    const tpl = mensajeDeCambioDeVida(W, tipo, idioma) || {};
    const porDefecto = idioma === 'es' ? DEFAULT_ES : DEFAULT_EN;
    setSubject(fill(tpl.subject?.trim() || porDefecto.subject, vars));
    // El mensaje se edita con el editor de correo, que trabaja en HTML. Una
    // plantilla escrita con él ya lo es: los datos del cliente entran
    // escapados. Una de texto plano se rellena y se convierte linea a linea.
    const plantilla = tpl.body?.trim() || porDefecto.body;
    setBody(esDocumentoConFormato(plantilla)
      ? fill(plantilla, Object.fromEntries(Object.entries(vars).map(([k, v]) => [k, esc(v ?? '')])))
      : aHtmlDeDocumento(fill(plantilla, vars)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ajustes, tipo, idioma, vars.cliente, vars.compania, vars.tipo, vars.poliza, vars.plan, vars.ingreso, vars.prima, vars.efectividad]);

  // ¿El texto sale de una plantilla de la agencia o del de fábrica? Cambia lo
  // que hace el botón de idioma, así que se dice en pantalla.
  const W = (ajustes && ajustes.welcomeKitTemplates) || {};
  const dePlantilla = hayMensajePropio(W, tipo, idioma);
  const nombreDelIdioma = idioma === 'es' ? (es ? 'español' : 'Spanish') : (es ? 'inglés' : 'English');

  const marcados = Object.keys(sel).filter((id) => sel[id]);

  const nombreDelKit = tipo === 'general'
    ? (es ? 'General (todos los tipos)' : 'General (all types)')
    : (es ? (getPolicyType(tipo).labelEs || getPolicyType(tipo).label) : getPolicyType(tipo).label);

  const abrirEdicion = () => {
    const guardado = versionParaEditar(W, tipo, idioma);
    setPlAsunto(guardado.subject || '');
    setPlCuerpo(aHtmlDeDocumento(guardado.body || ''));
    setEditando(true);
  };

  const guardarPlantilla = async () => {
    if (guardandoPl) return;
    // Un editor vaciado deja "<div><br></div>": eso es un mensaje vacío, y
    // vacío significa volver al General (o al texto de fábrica).
    const cuerpo = String(plCuerpo || '').trim();
    const cuerpoVacio = !cuerpo.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').trim() && !/<img/i.test(cuerpo);
    const valor = { subject: String(plAsunto || '').trim(), body: cuerpoVacio ? '' : cuerpo };
    setGuardandoPl(true);
    try {
      // Sobre el mapa recién leído del servidor, no sobre la copia de cuando se
      // abrió esta ventana: guardar la copia vieja borraría lo que otra persona
      // hubiera guardado mientras (los kits de prospecto y renovación viven en
      // el mismo mapa).
      const fresco = await getAgencySettings();
      // Solo la versión del idioma elegido (E-420); la del otro no se toca.
      const siguiente = mapaConVersion((fresco && fresco.welcomeKitTemplates) || {}, tipo, idioma, valor);
      await updateAgencySettings({ welcomeKitTemplates: siguiente });
      setAjustes({ ...(fresco || {}), welcomeKitTemplates: siguiente });
      setEditando(false);
      toast.success(es ? 'Mensaje guardado para toda la agencia.' : 'Message saved for the whole agency.');
    } catch (err) {
      toast.error((es ? 'No se pudo guardar: ' : 'Could not save: ') + (err?.message || ''));
    } finally {
      setGuardandoPl(false);
    }
  };

  const enviar = async () => {
    if (!client?.email) { toast.error(es ? 'El cliente no tiene correo registrado.' : 'Client has no email on file.'); return; }
    const textoPlano = body
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\s*\/\s*(div|p|li|h[1-6]|blockquote)\s*>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ').replace(/&lt;/gi, '<').replace(/&gt;/gi, '>').replace(/&amp;/gi, '&')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    if (!textoPlano && !/<img/i.test(body)) { toast.error(es ? 'Escribe un mensaje.' : 'Write a message.'); return; }
    if (marcados.length > MAX_DOCS) {
      toast.error(es ? `Máximo ${MAX_DOCS} documentos por correo.` : `Maximum ${MAX_DOCS} documents per email.`);
      return;
    }
    // El cuerpo ya es HTML del editor, con sus propios parrafos: el servidor
    // (`ensureHtmlParagraphs`) lo deja tal cual. Nada de envolverlo en un
    // <div> con `white-space:pre-wrap`, que Gmail/Outlook ignoran (Gabriel De
    // Silva, 2026-09-28).
    const html = body;
    const payloadDocs = marcados.map((documentId) => ({ documentId, mode: 'link' }));
    const mandar = (ack) => sendClientEmail(client.id, {
      subject: subject.trim() || (es ? 'Cambio de vida' : 'Life change'),
      html, text: textoPlano, docs: payloadDocs, acknowledgePhi: ack,
    });
    setEnviando(true);
    try {
      await mandar(false);
      toast.success(es ? 'Kit de cambio de vida enviado.' : 'Life change kit sent.');
      onClose();
    } catch (err) {
      // Mismo trato que el correo normal de la ficha: si el servidor detecta
      // posible información sensible, se pregunta antes de insistir.
      if (err?.code === 'phi') {
        const ok = await confirmDialog({
          title: es ? 'Posible info sensible' : 'Possible sensitive info',
          message: es ? 'Se detectó posible información sensible en el mensaje. ¿Enviar de todos modos?' : 'Possible sensitive information was detected. Send anyway?',
        });
        if (ok) {
          try { await mandar(true); toast.success(es ? 'Kit enviado.' : 'Kit sent.'); onClose(); }
          catch (e2) { toast.error(e2.message || (es ? 'No se pudo enviar.' : 'Could not send.')); }
        }
      } else {
        const m = String(err?.message || err);
        if (/mailbox connected|no-integration/i.test(m)) {
          toast.error(es ? 'Conecta tu correo (Gmail u Outlook) en "Mi Correo" para poder enviar.' : 'Connect your mailbox (Gmail or Outlook) under "My Email" to send.');
        } else {
          toast.error((es ? 'No se pudo enviar: ' : 'Failed to send: ') + m);
        }
      }
    } finally { setEnviando(false); }
  };

  return (
    <Modal title={`🔄 ${es ? 'Kit de Cambio de Vida' : 'Life Change Kit'} — ${vars.cliente}`} onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
        <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5 }}>
          {es
            ? 'Se envía desde tu correo, con tu firma. Los enlaces de los documentos caducan en 7 días.'
            : 'Sent from your mailbox, with your signature. Document links expire in 7 days.'}
        </div>

        {/* Idioma y tipo, arriba y a la vista: antes no se veía en qué idioma
            salía el mensaje ni había forma de elegirlo por tipo de seguro
            (Lissette 2026-08-20, sobre el primer envío). */}
        <div style={{ display: 'flex', gap: '.8rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label className="dkt-input-lbl">{es ? 'Idioma del mensaje' : 'Message language'}</label>
            <div style={{ display: 'flex', gap: '.35rem' }}>
              {[['es', '🇪🇸 Español'], ['en', '🇺🇸 English']].map(([id, etiqueta]) => (
                <button key={id} type="button" onClick={() => setIdioma(id)} disabled={editando}
                  title={editando ? (es ? 'Guarda o vuelve antes de cambiar de idioma: cada idioma se edita aparte.' : 'Save or go back before switching language: each language is edited separately.') : undefined}
                  style={{
                    padding: '.35rem .75rem', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                    fontSize: '.78rem', fontWeight: 700,
                    border: `1px solid ${idioma === id ? 'rgba(0,212,255,.45)' : 'var(--c-border,#1a3550)'}`,
                    background: idioma === id ? 'var(--c-cyan-dim,rgba(0,212,255,.12))' : 'transparent',
                    color: idioma === id ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)',
                  }}>
                  {etiqueta}
                </button>
              ))}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 190 }}>
            <label className="dkt-input-lbl">{es ? 'Kit para' : 'Kit for'}</label>
            <select className="dkt-inp" value={tipo} onChange={(e) => setTipo(e.target.value)} disabled={editando}>
              <option value="general">{es ? '⭐ General (todos los tipos)' : '⭐ General (all types)'}</option>
              {/* Con los tipos propios de la agencia (Lissette 2026-10-19). */}
              {tiposDePolizaConKit().map((tid) => {
                const pt = getPolicyType(tid);
                return <option key={tid} value={tid}>{pt.icon} {es ? (pt.labelEs || pt.label) : pt.label}</option>;
              })}
            </select>
          </div>
        </div>
        <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: -4 }}>
          {dePlantilla
            ? (es
              ? `Este texto es la versión en ${nombreDelIdioma} del mensaje de tu agencia. Cada idioma tiene la suya.`
              : `This text is the ${nombreDelIdioma} version of your agency's message. Each language has its own.`)
            : (es
              ? `Tu agencia todavía no escribió la versión en ${nombreDelIdioma} de este mensaje, así que sale el texto de fábrica en ${nombreDelIdioma}.`
              : `Your agency has not written the ${nombreDelIdioma} version of this message yet, so the built-in ${nombreDelIdioma} text goes out.`)}
        </div>

        {!editando && (
          <button type="button" onClick={abrirEdicion} disabled={cargando}
            style={{ alignSelf: 'flex-start', fontSize: '.74rem', fontWeight: 700, color: 'var(--c-cyan,#00d4ff)', background: 'rgba(0,212,255,.08)', border: '1px solid rgba(0,212,255,.3)', borderRadius: 8, padding: '.35rem .7rem', cursor: 'pointer', fontFamily: 'inherit' }}>
            ✏️ {es ? `Editar el mensaje de la agencia — ${nombreDelKit} (${nombreDelIdioma})` : `Edit the agency message — ${nombreDelKit} (${nombreDelIdioma})`}
          </button>
        )}

        {editando ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem', border: '1px solid rgba(0,212,255,.3)', borderRadius: 10, padding: '.75rem' }}>
            <div style={{ fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', lineHeight: 1.5 }}>
              {es
                ? `Versión en ${nombreDelIdioma.toUpperCase()} del mensaje de la agencia para «${nombreDelKit}»: la que reciben los clientes a los que se les escribe en ${nombreDelIdioma}. La del otro idioma se edita aparte. Vale para TODOS los clientes. Usa los campos ({cliente}, {compania}…), no los datos de este cliente. ${tipo === 'general' ? 'Si lo dejas vacío, sale el texto de fábrica.' : 'Si lo dejas vacío, se usa el General.'}`
                : `${nombreDelIdioma.toUpperCase()} version of the agency message for "${nombreDelKit}": what clients written to in ${nombreDelIdioma} receive. The other language is edited separately. Applies to ALL clients. Use the fields ({cliente}, {compania}…), not this client's details. ${tipo === 'general' ? 'Leave it blank to use the built-in text.' : 'Leave it blank to use the General one.'}`}
            </div>
            <div>
              <label className="dkt-input-lbl">{es ? 'Asunto' : 'Subject'}</label>
              <input className="dkt-inp" value={plAsunto} onChange={(e) => setPlAsunto(e.target.value)}
                placeholder={idioma === 'es' ? DEFAULT_ES.subject : DEFAULT_EN.subject} {...viPl.bind(() => plAsunto, setPlAsunto)} />
            </div>
            <div>
              <label className="dkt-input-lbl">{es ? 'Mensaje' : 'Message'}</label>
              <EmailRichEditor value={plCuerpo} onChange={setPlCuerpo} inserter={viPl} lang={es ? 'es' : 'en'} hoja minHeight={200} />
              <VarChips vars={chipsDelKit(es ? 'es' : 'en')} inserter={viPl} lang={es ? 'es' : 'en'}
                label={es ? 'Campos — haz clic para insertar:' : 'Fields — click to insert:'} style={{ marginTop: 6 }} />
            </div>
            <div style={{ display: 'flex', gap: '.5rem' }}>
              <button type="button" className="dkt-btn-p" onClick={guardarPlantilla} disabled={guardandoPl}
                style={{ flex: 1, justifyContent: 'center', opacity: guardandoPl ? .6 : 1 }}>
                {guardandoPl ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar para toda la agencia' : 'Save for the whole agency')}
              </button>
              <button type="button" className="dkt-btn-s" onClick={() => setEditando(false)} disabled={guardandoPl}>
                {es ? 'Volver' : 'Back'}
              </button>
            </div>
          </div>
        ) : (<>
        <div>
          <label className="dkt-input-lbl">{es ? 'Para' : 'To'}</label>
          <input className="dkt-inp" value={client?.email || (es ? '(sin correo)' : '(no email)')} readOnly />
        </div>
        <div>
          <label className="dkt-input-lbl">{es ? 'Asunto' : 'Subject'}</label>
          <input className="dkt-inp" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={cargando}
            {...vi.bind(() => subject, setSubject)} />
        </div>
        <div>
          <label className="dkt-input-lbl">{es ? 'Mensaje' : 'Message'}</label>
          <EmailRichEditor value={body} onChange={setBody} inserter={vi} lang={es ? 'es' : 'en'} hoja minHeight={200} disabled={cargando} />
          {/* Fichas para hacer clic, como en el editor del kit y en Firmas.
              Este kit no tenia ninguna (Lissette 2026-09-01: "ni no tiene
              ninguno"), asi que el enlace del portal habia que escribirlo de
              memoria. */}
          <VarChips
            vars={chipsDelKit(es ? 'es' : 'en')}
            inserter={vi}
            lang={es ? 'es' : 'en'}
            label={es ? 'Campos — haz clic para insertar:' : 'Fields — click to insert:'}
            style={{ marginTop: 6 }}
          />
          <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginTop: 5 }}>
            {es
              ? 'El texto sale del Kit de Cambio de Vida de tu agencia. Puedes cambiarlo aquí solo para este envío. {portal} se convierte en el enlace al portal del cliente al enviar.'
              : "The text comes from your agency's Life Change Kit. You can tweak it here for this send only. {portal} becomes the client's portal link on send."}
          </div>
        </div>

        <div style={{ border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, padding: '.6rem .7rem' }}>
          <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '.45rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Paperclip size={12} /> {es ? `Documentos del kit (${marcados.length})` : `Kit documents (${marcados.length})`}
          </div>
          {kitDocs.length === 0 ? (
            <div style={{ fontSize: '.76rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.5 }}>
              {es
                ? 'Este cliente no tiene documentos en el Kit de Cambio de Vida. Se marcan en su pestaña Documentos, con el botón "Kit ▾". El mensaje se puede enviar igual.'
                : 'This client has no documents in the Life Change Kit. Tag them in their Documents tab with the "Kit ▾" button. The message can still be sent.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.25rem', maxHeight: 180, overflowY: 'auto' }}>
              {kitDocs.map((d) => (
                <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!sel[d.id]} onChange={(e) => setSel((p) => ({ ...p, [d.id]: e.target.checked }))}
                    style={{ accentColor: 'var(--c-cyan,#00d4ff)', cursor: 'pointer' }} />
                  <span>{d.name}</span>
                </label>
              ))}
            </div>
          )}
          {marcados.length > MAX_DOCS && (
            <div style={{ fontSize: '.72rem', color: 'var(--c-amber,#ffb700)', marginTop: '.4rem' }}>
              {es ? `Solo caben ${MAX_DOCS} documentos por correo. Desmarca ${marcados.length - MAX_DOCS}.` : `Only ${MAX_DOCS} documents fit per email. Untick ${marcados.length - MAX_DOCS}.`}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '.5rem' }}>
          <button type="button" className="dkt-btn-p" onClick={enviar} disabled={enviando || cargando || !client?.email}
            style={{ flex: 1, justifyContent: 'center', opacity: (enviando || cargando || !client?.email) ? .6 : 1 }}>
            <Send size={14} /> {enviando ? (es ? 'Enviando…' : 'Sending…') : (es ? 'Enviar kit' : 'Send kit')}
          </button>
          <button type="button" className="dkt-btn-s" onClick={onClose} disabled={enviando}>
            {es ? 'Cancelar' : 'Cancel'}
          </button>
        </div>
        </>)}
      </div>
    </Modal>
  );
}
